/* eslint-disable react/jsx-no-bind */
import {
  Settings,
  Delete,
  RestoreFromTrash,
  Refresh,
  Search,
  Clear,
  WarningAmber,
} from '@mui/icons-material';
import {
  Box,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem as DropdownItem,
  Paper,
  IconButton,
  Menu,
  CardContent,
  Card,
  TablePagination,
  Modal,
  Typography,
  useTheme,
  Divider,
  Grid,
  Switch,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { tokens } from '../../theme';
import { formatToIST } from '../../utils/dateFormatter';

import vehicleService from './vehicleService';

const VehicleType = () => {
  const [apiResponse, setApiResponse] = useState({
    status: false,
    message: '',
    data: { vehicles: [] },
    status_code: 0,
  });
  const [search, setSearch] = useState('');
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [modalOpen, setModalOpen] = useState(false);
  const [newVehicleType, setNewVehicleType] = useState({
    vehicle_name: '',
    description: '',
    vehicle_icon: null,
    isHeavyVehicle: false,
    maxDimension: 0,
    baseFare: 0,
    perKmRate: 0,
    perKmThreshold: 0,
    shortCode: '',
  });
  const [selectedVehicleType, setSelectedVehicleType] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [errors, setErrors] = useState({
    vehicleName: '',
    vehicleIcon: '',
    description: '',
    shortCode: '',
  });
  const [tableLoading, setTableLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const vehicleData = apiResponse.data?.vehicles || [];
  const filteredData = vehicleData.filter(
    (item) =>
      (search === '' ||
        item.vehicleName.toLowerCase().includes(search.toLowerCase())) &&
      (showDeleted ? item.isDeleted : !item.isDeleted),
  );

  const fetchData = async () => {
    setTableLoading(true);
    try {
      const res = await vehicleService.fetchVehicleTypes();
      setApiResponse({
        status: res.data.status,
        message: res.data.message,
        data: { vehicles: res.data.data.vehicles },
        status_code: res.data.status_code,
      });
      setPage(0);
    } catch (err) {
      toast.error(
        err.response?.data?.message || 'Error fetching vehicle types',
      );
      setApiResponse({
        status: false,
        message: err.message,
        data: { vehicles: [] },
        status_code: err.response?.status || 500,
      });
    } finally {
      setTableLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [showDeleted]);

  const handleRefresh = () => {
    fetchData();
    toast.success('Data refreshed successfully');
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(0);
  };

  const handleClearSearch = () => {
    setSearch('');
    setPage(0);
  };

  const handleMenuOpen = (event, row) => {
    setMenuAnchor(event.currentTarget);
    setSelectedVehicleType(row);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isFormValid = () => {
    if (isEditMode) {
      // In edit mode, all fields are optional
      return true;
    }

    // Vehicle Create Mode - all fields required

    if (!newVehicleType.vehicle_name?.trim()) {
      return false;
    }
    const vehicleNameRegex = /^[A-Za-z\s]+$/;
    if (!vehicleNameRegex.test(newVehicleType.vehicle_name.trim())) {
      return false;
    }

    if (!newVehicleType.vehicle_icon) {
      return false;
    }

    if (!newVehicleType.description?.trim()) {
      return false;
    }

    if (!newVehicleType.shortCode?.trim()) {
      return false;
    }


    const maxDimension = Number(newVehicleType.maxDimension);
    if (isNaN(maxDimension) || maxDimension <= 0) {
      return false;
    }

    const baseFare = Number(newVehicleType.baseFare);
    if (isNaN(baseFare) || baseFare <= 0) {
      return false;
    }

    const perKmRate = Number(newVehicleType.perKmRate);
    if (isNaN(perKmRate) || perKmRate <= 0) {
      return false;
    }

    const perKmThreshold = Number(newVehicleType.perKmThreshold);
    if (isNaN(perKmThreshold) || perKmThreshold <= 0) {
      return false;
    }

    return true;
  };

  const validateInputs = () => {
    let valid = true;
    const newErrors = {
      vehicle_name: '',
      vehicle_icon: '',
      description: '',
      shortCode: '',
    };

    // Vehicle name format: when provided, must be letters and spaces only (both create and edit)
    const vehicleNameTrimmed = (newVehicleType.vehicle_name || '').trim();
    if (vehicleNameTrimmed) {
      const vehicleNameFormatRegex = /^[A-Za-z\s]+$/;
      if (!vehicleNameFormatRegex.test(vehicleNameTrimmed)) {
        newErrors.vehicle_name =
          'Vehicle type must contain only letters and spaces (no numbers or special characters).';
        valid = false;
      }
    }

    if (isEditMode) {
      // in vehicle edit mode all fields are optional

    } else {
      // Create mode: required fields in vehicle create mode
      if (!newVehicleType.vehicle_name.trim()) {
        newErrors.vehicle_name = 'Name is required.';
        valid = false;
      }

      if (!newVehicleType.vehicle_icon) {
        newErrors.vehicle_icon = 'Vehicle icon required.';
        valid = false;
      }
    }

    // Image size must be below 1MB when a file is selected
    if (newVehicleType.vehicle_icon instanceof File && newVehicleType.vehicle_icon.size > 1024 * 1024) {
      newErrors.vehicle_icon = 'Image size must be below 1MB.';
      valid = false;
    }

    if (!isEditMode) {
      if (!newVehicleType.description.trim()) {
        newErrors.description = 'Description is required.';
        valid = false;
      }

      if (!newVehicleType.shortCode.trim()) {
        newErrors.shortCode = 'Short code is required.';
        valid = false;
      }
    }

    // Short code: when provided (required in create, optional in edit), must be 2-5 letters only (e.g. TOY, DOC, FRU)
    const shortCodeTrimmed = (newVehicleType.shortCode || '').trim();
    if (shortCodeTrimmed) {
      const shortCodeRegex = /^[A-Za-z]{2,5}$/;
      if (!shortCodeRegex.test(shortCodeTrimmed)) {
        if (/^\d+$/.test(shortCodeTrimmed)) {
          newErrors.shortCode =
            'Short code must be letters only (e.g. SUV, BUS, CAR), not numbers.';
        } else if (shortCodeTrimmed.length < 2 || shortCodeTrimmed.length > 5) {
          newErrors.shortCode = 'Short code must be 2–5 characters.';
        } else {
          newErrors.shortCode =
            'Short code must be 2–5 letters only (e.g. SUV, BUS, CAR).';
        }
        valid = false;
      }
    }

    setErrors(newErrors);
    return valid;
  };

  const handleModalOpen = (editMode = false) => {
    setIsEditMode(editMode);
    if (editMode && selectedVehicleType) {
      setNewVehicleType({
        vehicle_name:
          selectedVehicleType.vehicleName || selectedVehicleType.vehicle_name,
        description: selectedVehicleType.description,
        vehicle_icon:
          selectedVehicleType.vehicleIcon || selectedVehicleType.vehicle_icon,
        isHeavyVehicle: selectedVehicleType.isHeavyVehicle,
        maxDimension: selectedVehicleType.maxDimension,
        baseFare: selectedVehicleType.baseFare,
        perKmRate: selectedVehicleType.perKmRate,
        perKmThreshold: selectedVehicleType.perKmThreshold,
        shortCode: selectedVehicleType.shortCode,
      });
    } else {
      setNewVehicleType({
        vehicle_name: '',
        vehicle_icon: null,
        description: '',
        isHeavyVehicle: false,
        maxDimension: 0,
        baseFare: 0,
        perKmRate: 0,
        perKmThreshold: 0,
        shortCode: '',
      });
    }
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setNewVehicleType({
      vehicle_name: '',
      vehicle_icon: null,
      description: '',
      isHeavyVehicle: false,
      maxDimension: 0,
      baseFare: 0,
      perKmRate: 0,
      perKmThreshold: 0,
      shortCode: '',
    });
    setErrors({
      vehicle_name: '',
      vehicle_icon: '',
      description: '',
      shortCode: '',
    });
    setSelectedVehicleType(null);
  };

  const handleAddVehicleType = (e) => {
    e.preventDefault();
    if (validateInputs()) {
      setActionLoading(true);

      const formData = new FormData();
      formData.append('typeName', newVehicleType.vehicle_name);
      // formData.append("vehicleName", newVehicleType.vehicle_name);
      formData.append('description', newVehicleType.description);
      formData.append('isHeavyVehicle', String(newVehicleType.isHeavyVehicle));
      formData.append('maxDimension', String(newVehicleType.maxDimension));
      formData.append('baseFare', String(newVehicleType.baseFare));
      formData.append('perKmRate', String(newVehicleType.perKmRate));
      formData.append('perKmThreshold', String(newVehicleType.perKmThreshold));
      formData.append('shortcode', newVehicleType.shortCode);

      if (newVehicleType.vehicle_icon instanceof File) {
        formData.append('vehicleIcon', newVehicleType.vehicle_icon);
      }

      vehicleService
        .addVehicle(formData)
        .then(() => {
          toast.success('Vehicle type created successfully.!');
          return fetchData();
        })
        .then(() => handleModalClose())
        .catch((error) => {
          toast.error(
            error.response?.data?.message || 'Error adding vehicle type',
          );
        })
        .finally(() => setActionLoading(false));
    }
  };

  const handleEditVehicleType = (e) => {
    e.preventDefault();
    if (validateInputs()) {
      // Ensure we have a vehicle ID
      if (!selectedVehicleType?.vehicleId) {
        toast.error('Vehicle ID is missing. Please try again.');
        return;
      }

      setActionLoading(true);
      const queryParams = {
        vehicleId: String(selectedVehicleType.vehicleId), // Include vehicle ID in payload
        typeName: String(newVehicleType.vehicle_name || '').trim(),
        description: String(newVehicleType.description || '').trim(),
        isHeavyVehicle: String(newVehicleType.isHeavyVehicle),
        maxDimension: String(newVehicleType.maxDimension),
        baseFare: String(newVehicleType.baseFare),
        perKmRate: String(newVehicleType.perKmRate),
        perKmThreshold: String(newVehicleType.perKmThreshold),
        shortCode: String(newVehicleType.shortCode || '').trim(),
      };

      let formData = null;
      if (newVehicleType.vehicle_icon instanceof File) {
        formData = new FormData();
        formData.append('vehicleIcon', newVehicleType.vehicle_icon);
      }

      vehicleService
        .updateVehicle(selectedVehicleType.vehicleId, queryParams, formData)
        .then(() => {
          toast.success('Vehicle type updated successfully');
          return fetchData();
        })
        .then(() => handleModalClose())
        .catch((error) => {
          toast.error(
            error.response?.data?.message || 'Error editing vehicle type',
          );
        })
        .finally(() => setActionLoading(false));
    }
  };

  const handleDelete = () => {
    setActionLoading(true);
    vehicleService
      .deleteVehicle(selectedVehicleType.vehicleId)
      .then(() => {
        toast.success('Deleted permanently');
        return fetchData();
      })
      .then(() => handleMenuClose())
      .catch((error) => {
        toast.error(
          error.response?.data?.message || 'Error deleting vehicle type',
        );
      })
      .finally(() => setActionLoading(false));
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = () => {
    setActionLoading(true);
    vehicleService
      .softDeleteVehicle(selectedVehicleType.vehicleId)
      .then(() => {
        toast.success(
          `Vehicle ID ${selectedVehicleType.vehicleId} deleted successfully.`,
        );
        return fetchData();
      })
      .then(() => {
        setDeleteDialogOpen(false);
        setSelectedVehicleType(null);
      })
      .catch((error) => {
        toast.error(
          error.response?.data?.message || 'Error soft deleting vehicle type',
        );
      })
      .finally(() => setActionLoading(false));
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedVehicleType(null);
  };

  const handleRestore = () => {
    setActionLoading(true);
    vehicleService
      .restoreVehicle(selectedVehicleType.vehicleId)
      .then(() => {
        toast.success('Vehicle restored successfully');
        return fetchData();
      })
      .then(() => handleMenuClose())
      .catch((error) => {
        toast.error(
          error.response?.data?.message || 'Error restoring vehicle type',
        );
      })
      .finally(() => setActionLoading(false));
  };

  return (
    <>
      <Card
        sx={{
          boxShadow: 2,
          borderRadius: 2,
          padding: 1,
          margin: 2,
          marginLeft: 4,
        }}
      >
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingLeft: 4,
              paddingTop: 2,
              paddingBottom: 0,
            }}
          >
            <h3>Vehicle Type</h3>
            {/* <Box display="flex" alignItems="center" gap={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={showDeleted}
                    onChange={() => setShowDeleted(!showDeleted)}
                    color="primary"
                  />
                }
                label="Show Deleted"
              /> */}
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={handleRefresh}
              disabled={tableLoading}
              sx={{
                textTransform: 'none',
                borderColor: colors.swiftcarry[100],
                color: colors.swiftcarry[100],
                '&:hover': {
                  borderColor: colors.swiftcarry[200],
                  backgroundColor: 'transparent',
                },
              }}
            >
              Refresh
            </Button>
          </Box>
          {/* </Box> */}
          <Divider />
          <Box sx={{ padding: 4 }}>
            <Grid
              container
              spacing={2}
              alignItems="center"
              sx={{ marginBottom: 3 }}
            >
              <Grid item xs={12} sm={8} md={9}>
                <TextField
                  label="Search"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={search}
                  onChange={handleSearchChange}
                  placeholder="Search by vehicle name"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ fontSize: 20, color: 'gray' }} />
                      </InputAdornment>
                    ),
                    endAdornment: search && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={handleClearSearch}
                          sx={{ padding: '4px' }}
                        >
                          <Clear sx={{ fontSize: 18 }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: '40px',
                      borderRadius: '8px',
                      backgroundColor: 'white',
                      '&.Mui-focused fieldset': {
                        borderColor: colors.swiftcarry[100],
                        borderWidth: '2px',
                      },
                      '&:hover fieldset': {
                        borderColor: colors.swiftcarry[100],
                      },
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '14px',
                      padding: '10px 14px',
                    },
                    '& .MuiInputLabel-root': {
                      color: 'gray',
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: colors.swiftcarry[100],
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={3}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => handleModalOpen(false)}
                  fullWidth
                  sx={{
                    bgcolor: colors.swiftcarry[100],
                    '&:hover': {
                      bgcolor: colors.swiftcarry[100],
                    },
                    height: '40px',
                  }}
                >
                  Add Vehicle
                </Button>
              </Grid>
            </Grid>

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell style={{ fontWeight: 'bold' }}>
                      Vehicle ID
                    </TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>
                      Vehicle Name
                    </TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>
                      Short Code
                    </TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>
                      Vehicle Icon
                    </TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>
                      Heavy Vehicle
                    </TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>
                      Max Dimension
                    </TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>
                      Base Fare
                    </TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>
                      Per KM Rate
                    </TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>
                      Per KM Threshold
                    </TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>
                      Created On
                    </TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>
                      Created By
                    </TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tableLoading ? (
                    <TableRow>
                      <TableCell colSpan={12} align="center">
                        <CircularProgress
                          size={24}
                          sx={{ color: colors.swiftcarry[100] }}
                        />
                      </TableCell>
                    </TableRow>
                  ) : filteredData.length > 0 ? (
                    filteredData
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage,
                      )
                      .map((row) => (
                        <TableRow key={row.vehicleId}>
                          <TableCell>{row.vehicleId}</TableCell>
                          <TableCell>{row.vehicleName}</TableCell>
                          <TableCell>{row.shortCode ?? '—'}</TableCell>
                          <TableCell>
                            <img
                              src={row.vehicleIcon}
                              alt={row.vehicleName}
                              style={{
                                width: '50px',
                                height: '50px',
                                objectFit: 'cover',
                                borderRadius: '5px',
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            {row.isHeavyVehicle ? 'Yes' : 'No'}
                          </TableCell>
                          <TableCell>{row.maxDimension ?? '—'}</TableCell>
                          <TableCell>{row.baseFare}</TableCell>
                          <TableCell>{row.perKmRate}</TableCell>
                          <TableCell>{row.perKmThreshold ?? '—'}</TableCell>
                          <TableCell>{formatToIST(row.createdOn)}</TableCell>
                          <TableCell>{row.createdBy || 'Unknown'}</TableCell>
                          <TableCell>
                            <IconButton
                              onClick={(event) => handleMenuOpen(event, row)}
                              disabled={actionLoading}
                            >
                              <Settings />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={12} align="center">
                        No vehicle types found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 50, 100]}
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                '& .MuiToolbar-root': {
                  display: 'flex',
                  width: '500px',
                  flexWrap: 'wrap',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                },
                '& .MuiTablePagination-selectLabel': {
                  marginBottom: '0px',
                },
                '& .MuiTablePagination-displayedRows': {
                  marginBottom: '0px',
                },
                '& .MuiTablePagination-actions': {
                  display: 'flex',
                  alignItems: 'center',
                  gap: '2px',
                },
                '& .MuiTablePagination-input': {
                  minWidth: '30px',
                },
              }}
            />
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={handleMenuClose}
            >
              {actionLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress size={20} />
                </Box>
              ) : (
                <>
                  <DropdownItem
                    onClick={() => {
                      handleModalOpen(true);
                      handleMenuClose();
                    }}
                  >
                    Update
                  </DropdownItem>
                  {selectedVehicleType?.isDeleted ? (
                    <DropdownItem onClick={handleRestore}>
                      <RestoreFromTrash fontSize="small" sx={{ mr: 1 }} />
                      Restore
                    </DropdownItem>
                  ) : (
                    <DropdownItem onClick={handleDeleteClick}>
                      {/* <Delete fontSize="small" sx={{ mr: 1 }} /> */}
                      Delete
                    </DropdownItem>
                  )}
                  {/* <DropdownItem onClick={handleDelete}>
                    <Delete fontSize="small" sx={{ mr: 1 }} />
                    Permanent Delete
                  </DropdownItem> */}
                </>
              )}
            </Menu>
          </Box>
        </CardContent>
      </Card>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            minWidth: { xs: '90%', sm: 400 },
            maxWidth: 500,
          },
        }}
      >
        <DialogTitle
          id="delete-dialog-title"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            pb: 2,
            pt: 3,
            px: 3,
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              bgcolor: 'error.light',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <WarningAmber
              sx={{
                fontSize: 28,
                color: 'error.main',
              }}
            />
          </Box>
          <Typography
            variant="h5"
            component="span"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              letterSpacing: '-0.02em',
            }}
          >
            Confirm Delete
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 2 }}>
          <DialogContentText
            id="delete-dialog-description"
            sx={{
              fontSize: '15px',
              lineHeight: 1.6,
              color: 'text.secondary',
              mb: 1,
            }}
          >
            Are you sure you want to delete the vehicle type{' '}
            <Box
              component="span"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
              }}
            >
              "{selectedVehicleType?.vehicleName}"
            </Box>
            ?
          </DialogContentText>
          <DialogContentText
            sx={{
              fontSize: '14px',
              color: 'error.main',
              fontWeight: 500,
              mt: 1,
            }}
          >
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: 2,
            gap: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Button
            onClick={handleDeleteCancel}
            disabled={actionLoading}
            variant="outlined"
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              px: 3,
              py: 1,
              fontWeight: 500,
              borderColor: 'text.secondary',
              color: 'text.secondary',
              minWidth: 100,
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                borderColor: 'text.primary',
                color: 'text.primary',
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                transform: 'translateY(-1px)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            disabled={actionLoading}
            variant="contained"
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              px: 3,
              py: 1,
              fontWeight: 600,
              bgcolor: 'error.main',
              minWidth: 100,
              boxShadow: '0 4px 12px rgba(211, 47, 47, 0.3)',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                bgcolor: 'error.dark',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 16px rgba(211, 47, 47, 0.4)',
              },
              '&:disabled': {
                bgcolor: 'action.disabledBackground',
                boxShadow: 'none',
              },
            }}
          >
            {actionLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Improved Modal */}
      <Modal open={modalOpen} onClose={handleModalClose}>
        <Box
          component="form"
          onSubmit={isEditMode ? handleEditVehicleType : handleAddVehicleType}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: '600px' },
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            outline: 'none',
            maxHeight: '95vh',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography
            variant="h5"
            component="h2"
            sx={{ mb: 3, fontWeight: 600 }}
          >
            {isEditMode ? 'Edit Vehicle Type' : 'Add Vehicle Type'}
          </Typography>

          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              overflowY: 'auto',
              pr: 1,
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#ddd',
                borderRadius: '3px',
              },
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label={isEditMode ? 'Vehicle Name' : 'Vehicle Name *'}
                  variant="outlined"
                  fullWidth
                  size="medium"
                  value={newVehicleType.vehicle_name}
                  onChange={(e) => {
                    // Restrict to letters and spaces only (meaningful text)
                    const value = e.target.value.replace(/[^A-Za-z\s]/g, '');
                    setNewVehicleType({
                      ...newVehicleType,
                      vehicle_name: value,
                    });
                    setErrors({ ...errors, vehicle_name: '' });
                  }}
                  error={!!errors.vehicle_name}
                  helperText={errors.vehicle_name}
                  placeholder="e.g. Mini Van, SUV"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label={isEditMode ? 'Short Code' : 'Short Code *'}
                  fullWidth
                  size="medium"
                  placeholder="e.g. BUS, CAR"
                  inputProps={{ maxLength: 5 }}
                  value={newVehicleType.shortCode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^A-Za-z]/g, '').toUpperCase();
                    setNewVehicleType({
                      ...newVehicleType,
                      shortCode: value,
                    });
                    setErrors({ ...errors, shortCode: '' });
                  }}
                  error={!!errors.shortCode}
                  helperText={errors.shortCode}
                />
              </Grid>
            </Grid>

            <TextField
              label={isEditMode ? 'Description' : 'Description *'}
              variant="outlined"
              fullWidth
              multiline
              rows={3}
              size="medium"
              value={newVehicleType.description}
              onChange={(e) => {
                setNewVehicleType({
                  ...newVehicleType,
                  description: e.target.value,
                });
                setErrors({ ...errors, description: '' });
              }}
              error={!!errors.description}
              helperText={errors.description}
            />

            <Box
              sx={{
                border: '1px solid #eee',
                borderRadius: 1,
                p: 2,
                backgroundColor: '#f9f9f9',
              }}
            >
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
                Vehicle Specifications
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={newVehicleType.isHeavyVehicle}
                        onChange={(e) =>
                          setNewVehicleType({
                            ...newVehicleType,
                            isHeavyVehicle: e.target.checked,
                          })
                        }
                        color="primary"
                      />
                    }
                    label="Heavy Vehicle"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    label={isEditMode ? 'Max Dimension' : 'Max Dimension *'}
                    type="number"
                    fullWidth
                    size="medium"
                    value={newVehicleType.maxDimension}
                    onChange={(e) =>
                      setNewVehicleType({
                        ...newVehicleType,
                        maxDimension: e.target.value,
                      })
                    }
                  />
                </Grid>
              </Grid>
            </Box>

            <Box
              sx={{
                border: '1px solid #eee',
                borderRadius: 1,
                p: 2,
                backgroundColor: '#f9f9f9',
              }}
            >
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
                Pricing Details
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={isEditMode ? 'Base Fare' : 'Base Fare *'}
                    type="number"
                    fullWidth
                    size="medium"
                    value={newVehicleType.baseFare}
                    onChange={(e) =>
                      setNewVehicleType({
                        ...newVehicleType,
                        baseFare: e.target.value,
                      })
                    }
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    label={isEditMode ? 'Per KM Rate' : 'Per KM Rate *'}
                    type="number"
                    fullWidth
                    size="medium"
                    value={newVehicleType.perKmRate}
                    onChange={(e) =>
                      setNewVehicleType({
                        ...newVehicleType,
                        perKmRate: e.target.value,
                      })
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label={isEditMode ? 'Per KM Threshold' : 'Per KM Threshold *'}
                    type="number"
                    fullWidth
                    size="medium"
                    value={newVehicleType.perKmThreshold}
                    onChange={(e) =>
                      setNewVehicleType({
                        ...newVehicleType,
                        perKmThreshold: e.target.value,
                      })
                    }
                  />
                </Grid>
              </Grid>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                Vehicle Icon {!isEditMode && '*'}
              </Typography>

              {newVehicleType.vehicle_icon && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    mb: 2,
                  }}
                >
                  <img
                    src={
                      typeof newVehicleType.vehicle_icon === 'string'
                        ? newVehicleType.vehicle_icon
                        : URL.createObjectURL(newVehicleType.vehicle_icon)
                    }
                    alt="Vehicle Preview"
                    style={{
                      width: '80px',
                      height: '80px',
                      objectFit: 'cover',
                      borderRadius: 8,
                      border: '1px solid #eee',
                    }}
                  />
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={() =>
                      setNewVehicleType({
                        ...newVehicleType,
                        vehicle_icon: null,
                      })
                    }
                  >
                    Remove
                  </Button>
                </Box>
              )}

              <Button
                component="label"
                variant="outlined"
                fullWidth
                size="medium"
                sx={{
                  textTransform: 'none',
                  borderStyle: newVehicleType.vehicle_icon ? 'dashed' : 'solid',
                }}
              >
                {newVehicleType.vehicle_icon ? 'Change Image' : 'Upload Image'}
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => {
                    if (e.target.files[0]) {
                      setNewVehicleType({
                        ...newVehicleType,
                        vehicle_icon: e.target.files[0],
                      });
                      setErrors({ ...errors, vehicle_icon: '' });
                    }
                  }}
                />
              </Button>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, display: 'block' }}
              >
                Image size must be below 1MB.
              </Typography>
              {errors.vehicle_icon && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.5, display: 'block' }}
                >
                  {errors.vehicle_icon}
                </Typography>
              )}
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
              pt: 3,
              mt: 'auto',
            }}
          >
            <Button
              variant="outlined"
              onClick={handleModalClose}
              disabled={actionLoading}
              sx={{ minWidth: 100 }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={actionLoading || !isFormValid()}
              sx={{
                minWidth: 100,
                backgroundColor: '#1976d2',
                '&:hover': {
                  backgroundColor: '#1565c0',
                },
              }}
            >
              {actionLoading ? (
                <CircularProgress size={24} sx={{ color: 'white' }} />
              ) : isEditMode ? (
                'Save Changes'
              ) : (
                'Add Vehicle'
              )}
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default VehicleType;
